import { memo, useCallback } from 'react';
import classes from 'classnames';
import HeadShake from 'react-reveal-effects/HeadShake';
import CheckIcon from '@/assets/icons/check-circle.svg?react';
import ErrorIcon from '@/assets/icons/error-circle.svg?react';
import ErrorMessage from '@/components/ErrorMessage/ErrorMessage';
import styles from './Select.module.scss';
import { EventCollectorProps } from '@/hooks/context/EventCollector/types';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import SelectEvent from '@/hooks/context/EventCollector/events/SelectEvent';

export type SelectionOptionType = Array<{
  name: string;
  value: string | number;
}>;

export type Props = {
  disabled?: boolean;
  value?: string | number;
  name?: string;
  error?: string;
  isTouched?: boolean;
  errorAnimationTrigger?: number; // Pass a different value to re-trigger error animation
  placeholder?: string;
  dataTest?: string;
  options?: SelectionOptionType;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  className?: string;
  autoFocus?: boolean;
  hideDisabledOption?: boolean;
  errorClassName?: string;
};

function Select({
  disabled,
  value = '',
  name,
  error,
  isTouched,
  errorAnimationTrigger,
  placeholder,
  dataTest = 'select',
  options,
  onChange,
  onBlur,
  className,
  autoFocus,
  hideDisabledOption,
  eventCollector,
  errorClassName,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();

  const showError = isTouched && error;
  const showCheck = isTouched && !error;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e);

      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new SelectEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? e.target.value,
          description: eventCollector?.description ?? name ?? '',
        }),
      );
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      name,
      onChange,
      track,
    ],
  );

  return (
    <div className={classes(styles.container, className)}>
      <div className={styles.field}>
        <select
          disabled={disabled}
          name={name}
          data-test={dataTest}
          value={value}
          onBlur={onBlur}
          onChange={handleChange}
          className={styles.select}
          autoFocus={autoFocus}
        >
          {!hideDisabledOption && (
            <option
              disabled
              data-test={`${dataTest}-option-disabled`}
              aria-label="option-disabled"
            />
          )}
          {options?.map(({ name: nameOption, value: valueOption }, index) => (
            <option
              key={valueOption}
              value={valueOption}
              data-test={`${dataTest}-option-${index}`}
            >
              {nameOption}
            </option>
          ))}
        </select>

        <label
          className={classes(
            styles.label,
            value.toString().length && styles.float,
          )}
          data-test={`${dataTest}-label`}
          htmlFor="select-label"
        >
          {placeholder}
        </label>

        <div className={styles.badges}>
          {(showError || showCheck) && <div className={styles.divider} />}
          {showError && (
            <HeadShake spy={errorAnimationTrigger || undefined}>
              <ErrorIcon
                className={classes(styles.badge, styles['error-icon'])}
                data-test={`${dataTest}-error-icon`}
              />
            </HeadShake>
          )}
          {showCheck && (
            <CheckIcon
              className={styles.badge}
              data-test={`${dataTest}-check-icon`}
            />
          )}
        </div>
      </div>
      {showError && (
        <div
          className={classes(styles['error-message'], errorClassName)}
          data-test={`${dataTest}-error-wrapper`}
        >
          <ErrorMessage errors={error} dataTest={`${dataTest}-error-message`} />
        </div>
      )}
    </div>
  );
}

const SelectMemo = memo(Select);

export default SelectMemo;
